import { useTeleconsultContext } from "../../Contexts/TeleconsultContext";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState, useEffect, useRef, useMemo } from "react";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import Button from "../../Components/Button";
import {
  ThemeProvider,
  createTheme,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

const TeleconsultLandlineNumber = () => {
  const { handleBackToDashboard, setShowHotlines, navigate } =
    useTeleconsultContext();
  const [selectedLocation, setSelectedLocation] = useState("");
  const [isMobileView, setIsMobileView] = useState(false);

  const [copiedState, setCopiedState] = useState(null);
  const scrollRef = useRef(null);

  const hotlineNumbers = {
    Baguio: "(074) 661 8832",
    Batangas: "(043) 779 8014",
    Cavite: "(046) 419 8016",
    Laguna: "(049) 559 8007",
    MetroManila: ["(02) 8582 1980", "(02) 7798 7798"],
    Naga: "(054) 871 2070",
    Palawan: "(048) 716 5122",
    Pampanga: "(045) 649 8005",

    Dumaguete: "(035) 522 5014",
    Bacolod: "(034) 458 6714",
    Iloilo: "(033) 328 7034",
    Ormoc: "(053) 832 9902",
    Cebu: "(032) 260 9068",

    CagayanDeOro: "(088) 864 8803",
    Kidapawan: "(064) 521 8002",
    Gensan: "(083) 887 9813",
    Davao: "(082) 238 7016",
  };

  const locationsByRegion = {
    Luzon: [
      "Baguio",
      "Batangas",
      "Cavite",
      "Laguna",
      "MetroManila",
      "Naga",
      "Palawan",
      "Pampanga",
    ],
    Visayas: ["Dumaguete", "Bacolod", "Iloilo", "Ormoc", "Cebu"],
    Mindanao: ["CagayanDeOro", "Kidapawan", "Gensan", "Davao"],
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 640);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [selectedLocation]);

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: "Lato",
          body1: "black",
        },
      }),
    []
  );

  const handleBack = () => {
    navigate(-1);
  };

  const handleAction = (phoneNumber) => {
    if (isMobileView) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      navigator.clipboard.writeText(phoneNumber).then(() => {
        setCopiedState(phoneNumber);
        setTimeout(() => {
          setCopiedState(null);
        }, 3000);
      });
    }
  };

  const resetHotlines = () => {
    setShowHotlines((prevState) => {
      const newState = {};
      Object.keys(prevState).forEach((key) => {
        newState[key] = false;
      });
      return newState;
    });
  };

  const setHotlinesForLocation = (location) => {
    setShowHotlines((prevState) => {
      const newState = { ...prevState };
      locationsByRegion[location].forEach((key) => {
        newState[key] = true;
      });
      return newState;
    });
  };

  const handleChangeLocation = (event) => {
    const location = event.target.value;
    setSelectedLocation(location);
    resetHotlines();
    setHotlinesForLocation(location);
  };

  return (
    <div className="flex flex-col min-h-screen max-w-screen sm:bg-[#E7EEFA] items-start sm:items-center">
      <div
        className={`flex flex-col w-full sm:w-148 sm:bg-white min-h-screen sm:min-h-0 items-start sm:items-center rounded-xl ${
          selectedLocation ? "sm:mt-3" : "sm:mt-24"
        }`}
      >
        <div className="w-full grid grid-cols-1 divide-y">
          <div className="p-4 relative">
            <KeyboardArrowLeftIcon
              className="absolute cursor-pointer"
              onClick={handleBack}
            />
            <div className="text-center font-semibold text-[#1F2328]">
              Talk to a Doctor
            </div>
          </div>

          <div className="p-4">
            <div className="text-[#60656D]">
              Use a landline phone or a mobile device with good signal strength
              and dial the following number:
            </div>

            <div className="mt-3">
              <div className="text-[#1F2328] font-bold">Mobile number:</div>
              <div className="w-full border border-[#E3E4E5] rounded-md p-2 flex items-center justify-between">
                <div>0919 0588 933</div>
                <div>
                  <Button
                    className={`h-7 transition ease-in-out delay-75 hover:scale-105 border rounded-md p-2 flex items-center cursor-pointer ${
                      isMobileView ? "bg-[#E7EEFA]" : "bg-transparent"
                    }`}
                    onClick={() => handleAction("0919 0588 933")}
                  >
                    {isMobileView ? (
                      <span className="bg-[#E7EEFA] text-[#0947AB] text-sm font-bold ml-1">
                        Call
                      </span>
                    ) : (
                      <>
                        {copiedState === "0919 0588 933" ? null : (
                          <ContentCopyIcon
                            sx={{ color: "#60656D" }}
                            fontSize="small"
                          />
                        )}
                        <span className="text-[#60656D] text-sm font-bold ml-1">
                          {copiedState === "0919 0588 933" ? "Copied!" : "Copy"}
                        </span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className="text-[#1F2328] font-bold mt-3">
              Landline number:
            </div>

            <div className="mt-2">
              <div className="flex flex-row">
                <div className="flex flex-col w-full">
                  <ThemeProvider theme={theme}>
                    <InputLabel id="id-location" className="font-bold">
                      Location
                    </InputLabel>
                    <Select
                      labelId="id-location"
                      id="select-location"
                      className="w-full h-12 mt-1"
                      value={selectedLocation}
                      onChange={handleChangeLocation}
                    >
                      <MenuItem value="Luzon">Luzon</MenuItem>
                      <MenuItem value="Visayas">Visayas</MenuItem>
                      <MenuItem value="Mindanao">Mindanao</MenuItem>
                    </Select>
                  </ThemeProvider>
                </div>
              </div>
            </div>

            {/* Scroll Area with Conditionally Rendered Content */}
            {selectedLocation && (
              <div className="mt-2">
                <ScrollArea
                  ref={scrollRef} // Assign the ref to the ScrollArea component
                  style={{
                    height: isMobileView ? null : "300px", //`${scrollAreaHeight}px`
                    overflowY: "auto",
                    // scrollbarWidth: "none", // Firefox
                    // msOverflowStyle: "none", // IE and Edge
                    // "&::-webkit-scrollbar": {
                    //   display: "none", // Chrome, Safari, Opera
                    // },
                  }}
                  verticalThumbProps={{ style: { backgroundColor: "#60656D" } }}
                >
                  {/* Render hotline numbers based on selected location */}
                  {locationsByRegion[selectedLocation].map(
                    (location) =>
                      hotlineNumbers[location] && (
                        <div key={location} className="flex flex-col mb-2">
                          <div className="w-full border border-[#E3E4E5] rounded-md p-2 flex flex-col">
                            <div className="font-bold ml-2">{`${location} hotline`}</div>
                            {Array.isArray(hotlineNumbers[location]) ? (
                              hotlineNumbers[location].map((number) => ( //index
                                <div
                                  key={number}
                                  className="flex items-center justify-between mt-3 ml-2"
                                >
                                  <div>{number}</div>
                                  <div>
                                    <Button
                                      className={`h-7 transition ease-in-out delay-75 hover:scale-105 border rounded-md p-2 flex items-center cursor-pointer ${
                                        isMobileView
                                          ? "bg-[#E7EEFA]"
                                          : "bg-transparent"
                                      }`}
                                      onClick={() => handleAction(number)}
                                    >
                                      {isMobileView ? (
                                        <span className="bg-[#E7EEFA] text-[#0947AB] text-sm font-bold ml-1">
                                          Call
                                        </span>
                                      ) : (
                                        <>
                                          {copiedState === number ? null : (
                                            <ContentCopyIcon
                                              sx={{ color: "#60656D" }}
                                              fontSize="small"
                                            />
                                          )}
                                          <span className="text-[#60656D] text-sm font-bold ml-1">
                                            {copiedState === number
                                              ? "Copied!"
                                              : "Copy"}
                                          </span>
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="flex items-center justify-between mt-3 ml-2">
                                <div>{hotlineNumbers[location]}</div>
                                <div>
                                  <Button
                                    className={`h-7 transition ease-in-out delay-75 hover:scale-105 border rounded-md p-2 flex items-center cursor-pointer ${
                                      isMobileView
                                        ? "bg-[#E7EEFA]"
                                        : "bg-transparent"
                                    }`}
                                    onClick={() =>
                                      handleAction(hotlineNumbers[location])
                                    }
                                  >
                                    {isMobileView ? (
                                      <span className="bg-[#E7EEFA] text-[#0947AB] text-sm font-bold ml-1">
                                        Call
                                      </span>
                                    ) : (
                                      <>
                                        {copiedState ===
                                        hotlineNumbers[location] ? null : (
                                          <ContentCopyIcon
                                            sx={{ color: "#60656D" }}
                                            fontSize="small"
                                          />
                                        )}
                                        <span className="text-[#60656D] text-sm font-bold ml-1">
                                          {copiedState ===
                                          hotlineNumbers[location]
                                            ? "Copied!"
                                            : "Copy"}
                                        </span>
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )
                  )}
                </ScrollArea>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white flex-grow" />
        <div className="flex flex-col sm:items-end items-center w-full px-4 mb-3">
          <Button
            className="h-12 w-full transition ease-in-out delay-75 rounded-xl text-white"
            onClick={handleBackToDashboard}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeleconsultLandlineNumber;
